import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { FormRoot, FormPrice, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";



import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, SettingsIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { walletApi } from "../api/client";
import { walletKeys } from "../api/queries";

const LimitsFormSchema = z.object({
  dailyTopupLimit: z
    .union([z.number().int().min(0, "ຕ້ອງ ≥ 0"), z.undefined(), z.nan()])
    .optional(),
  dailySpendLimit: z
    .union([z.number().int().min(0, "ຕ້ອງ ≥ 0"), z.undefined(), z.nan()])
    .optional(),
  maxBalance: z
    .union([z.number().int().min(0, "ຕ້ອງ ≥ 0"), z.undefined(), z.nan()])
    .optional(),
  requireApprovalOverLak: z
    .union([z.number().int().min(0, "ຕ້ອງ ≥ 0"), z.undefined(), z.nan()])
    .optional(),
});

type LimitsFormValues = z.infer<typeof LimitsFormSchema>;

function toNumOrNull(v: number | "" | undefined): number | null {
  if (v === undefined || v === "" || (typeof v === "number" && Number.isNaN(v)))
    return null;
  return Number(v) as number;
}

export function WalletLimitsPage() {
  const { walletId } = useParams({ from: "/app/wallet/limits/$walletId" });
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["wallet", "limits", walletId],
    queryFn: () => walletApi.getWalletLimits(walletId ?? ""),
    enabled: !!walletId,
  });

  const balanceLak = data?.balanceLak ?? 0;

  const schemaWithBalance = LimitsFormSchema.refine(
    (d) => {
      const max = toNumOrNull(d.maxBalance as number | undefined);
      return max === null || max >= balanceLak;
    },
    {
      message: `max_balance ບໍ່ຄວນຕ່ຳກວ່າຍອດຄົງເຫຼືອປັດຈຸບັນ (${balanceLak.toLocaleString("lo-LA")} LAK)`,
      path: ["maxBalance"],
    },
  );

  const methods = RHF.useForm<LimitsFormValues>({
    resolver: zodResolver(schemaWithBalance) as RHF.Resolver<LimitsFormValues>,
    defaultValues: {
      dailyTopupLimit: undefined,
      dailySpendLimit: undefined,
      maxBalance: undefined,
      requireApprovalOverLak: undefined,
    },
  });

  useEffect(() => {
    if (!data) return;
    methods.reset({
      dailyTopupLimit: data.dailyTopupLimit ?? undefined,
      dailySpendLimit: data.dailySpendLimit ?? undefined,
      maxBalance: data.maxBalance ?? undefined,
      requireApprovalOverLak: data.requireApprovalOverLak ?? undefined,
    });
  }, [data, methods]);

  const onSubmit = async (values: LimitsFormValues) => {
    if (!walletId) return;
    try {
      await walletApi.updateWalletLimits(walletId, {
        dailyTopupLimit: toNumOrNull(values.dailyTopupLimit),
        dailySpendLimit: toNumOrNull(values.dailySpendLimit),
        maxBalance: toNumOrNull(values.maxBalance),
        requireApprovalOverLak: toNumOrNull(values.requireApprovalOverLak),
      });
      toast.success("ບັນທຶກວົງເງິນແລ້ວ");
      qc.invalidateQueries({ queryKey: walletKeys.accountsList({}) });
      qc.invalidateQueries({ queryKey: ["wallet", "limits", walletId] });
    } catch {
      // fetcher already shows toast for API/network errors
    }
  };

  if (!walletId) {
    return null;
  }

  if (isLoading && !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ບັນຊີ Wallet",
                to: "/app/wallet/accounts",
              },
              {
                label: "ວົງເງິນ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ບັນຊີ Wallet",
                to: "/app/wallet/accounts",
              },
              {
                label: "ວົງເງິນ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive">
            ບໍ່ພົບບັນຊີ wallet ຫຼືເກີດຂໍ້ຜິດພາດ
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/accounts" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການບັນຊີ
          </Button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ບັນຊີ Wallet",
              to: "/app/wallet/accounts",
            },
            {
              label: "ວົງເງິນ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <SettingsIcon className="size-5" />
              ຕັ້ງວົງເງິນ (Limits)
            </h2>
            <p className="text-muted-foreground text-sm">
              ບັນຊີ: {data.customerName ?? "–"} · {data.customerPhone} · ຍອດຄົງເຫຼືອ{" "}
              {data.balanceLak.toLocaleString("lo-LA")} LAK
            </p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav({ to: "/app/wallet/accounts" })}
            className="gap-1 text-muted-foreground"
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການບັນຊີ
          </Button>
        </div>

        <Card className="mx-auto max-w-xl rounded-xl border">
          <CardHeader>
            <h3 className="font-semibold text-lg">ວົງເງິນຕໍ່ບັນຊີ</h3>
            <p className="text-muted-foreground text-sm">
              ປ່ອຍວ່າງເພື່ອບໍ່ຈຳກັດ. ຄ່າ 0 ຫຼືບວກເທົ່ານັ້ນ.
            </p>
          </CardHeader>
          <CardContent>
            <FormRoot<LimitsFormValues> methods={methods} onSubmit={onSubmit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormPrice
                  name="dailyTopupLimit"
                  label="ວົງເງິນເຕີມຕໍ່ວັນ (LAK)"
                  placeholder="ບໍ່ຈຳກັດ"
                  min={0}
                  currencySymbol="K"
                />
                <FormPrice
                  name="dailySpendLimit"
                  label="ວົງເງິນຊຳລະຕໍ່ວັນ (LAK)"
                  placeholder="ບໍ່ຈຳກັດ"
                  min={0}
                  currencySymbol="K"
                />
                <FormPrice
                  name="maxBalance"
                  label="ຍອດສູງສຸດ (LAK)"
                  placeholder="ບໍ່ຈຳກັດ"
                  min={balanceLak}
                  currencySymbol="K"
                />
                <FormPrice
                  name="requireApprovalOverLak"
                  label="ອະນຸມັດເມື່ອເກີນ (LAK)"
                  placeholder="ບໍ່ຕ້ອງອະນຸມັດ"
                  min={0}
                  currencySymbol="K"
                />
              </div>
              <div className="mt-6 flex gap-2">
                <Button type="submit">ບັນທຶກ</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => nav({ to: "/app/wallet/accounts" })}
                >
                  ຍົກເລີກ
                </Button>
              </div>
            </FormRoot>
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
